// components/MLPGameInterface.js
import React, { useState } from 'react';
import Court from './Court';
import Sidebar from './Sidebar';
import MatchSuggestionPopup from './MatchSuggestionPopup';
import { MLPGameManager } from '../services/MLPGameManager';

const MLPGameInterface = ({ teamData, onSessionComplete }) => {
  const [gameManager] = useState(() => new MLPGameManager(teamData));
  const [courts, setCourts] = useState(() => [
    { id: 1, players: [], timer: { startTime: null, isActive: false, duration: 15 * 60 }, isActive: false },
    { id: 2, players: [], timer: { startTime: null, isActive: false, duration: 15 * 60 }, isActive: false },
    { id: 3, players: [], timer: { startTime: null, isActive: false, duration: 15 * 60 }, isActive: false }
  ]);
  
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [suggestedMatch, setSuggestedMatch] = useState(null);
  const [waitingPlayers, setWaitingPlayers] = useState([]);

  // Convert team player string to name only (remove rating)
  const getPlayerName = (playerString) => {
    if (!playerString) return '';
    return playerString.split('(')[0].trim();
  };

  // Generate display names with duplicate handling
  const generateDisplayNames = () => {
    const allPlayerNames = Object.values(teamData).reduce((names, team) => {
      return [
        ...names,
        getPlayerName(team.m1),
        getPlayerName(team.m2), 
        getPlayerName(team.f1),
        getPlayerName(team.f2)
      ];
    }, []);

    const firstNameCounts = {};
    const nameMap = {};
    
    allPlayerNames.forEach(fullName => {
      const firstName = fullName.split(' ')[0];
      firstNameCounts[firstName] = (firstNameCounts[firstName] || 0) + 1;
    });

    allPlayerNames.forEach(fullName => {
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      if (firstNameCounts[firstName] > 1) {
        nameMap[fullName] = `${firstName} ${lastName.charAt(0)}`;
      } else {
        nameMap[fullName] = firstName;
      }
    });

    return nameMap;
  };

  const [displayNameMap] = useState(() => generateDisplayNames());

  const getDisplayName = (playerString) => {
    const fullName = getPlayerName(playerString);
    return displayNameMap[fullName] || fullName;
  };

  const getTeamName = (teamId) => {
    return teamData[teamId]?.name || `Team ${teamId}`;
  };

  // Find best available game from available players
  const findBestAvailableGame = (availablePlayerNames) => {
    console.log('=== ENHANCED DEBUG ===');
    console.log('Available players count:', availablePlayerNames.length);
    console.log('Available players:', availablePlayerNames);
    
    const allPendingGames = gameManager.getGamesByStatus('pending');
    console.log('Total pending games:', allPendingGames.length);
    
    if (allPendingGames.length > 0) {
      const firstGame = allPendingGames[0];
      const gamePlayerNames = [
        ...firstGame.players.team1.map(player => player.split('(')[0].trim()),
        ...firstGame.players.team2.map(player => player.split('(')[0].trim())
      ];
      console.log('First pending game needs players:', gamePlayerNames);
      console.log('Do we have these players?', gamePlayerNames.map(p => availablePlayerNames.includes(p)));
    }
    
    console.log('=== ROBUST GAME FINDER ===');
    console.log(`Available players (${availablePlayerNames.length}):`, availablePlayerNames);

    if (availablePlayerNames.length < 4) {
      console.log('Not enough players for any game (need at least 4)');
      return null;
    }

    // Find all games that CAN be played with available players
    const playableGames = [];

    for (const game of allPendingGames) {
      const gamePlayerNames = [
        ...game.players.team1.map(player => player.split('(')[0].trim()),
        ...game.players.team2.map(player => player.split('(')[0].trim())
      ];

      const allPlayersAvailable = gamePlayerNames.every(playerName => 
        availablePlayerNames.includes(playerName)
      );

      if (allPlayersAvailable) {
        console.log(`Game ${game.id} is playable: ${game.team1} vs ${game.team2} (${game.gameTypeLabel})`);
        console.log(`  Players: ${gamePlayerNames.join(', ')}`);
        playableGames.push(game);
      } else {
        const unavailablePlayers = gamePlayerNames.filter(player => !availablePlayerNames.includes(player));
        console.log(`Game ${game.id} not playable - missing: ${unavailablePlayers.join(', ')}`);
      }
    }

    if (playableGames.length === 0) {
      console.log('No games can be played with current available players');
      return null;
    }

    // Return the highest priority playable game
    const selectedGame = playableGames[0];
    console.log(`Selected Game ${selectedGame.id}: ${selectedGame.team1} vs ${selectedGame.team2}`);
    
    return selectedGame;
  };

  // Calculate current available players from provided state
  const calculateAvailablePlayersFromState = (currentCourts) => {
    console.log('=== CALCULATING AVAILABLE PLAYERS FROM PROVIDED STATE ===');
    
    // Get all active players from courts using provided state
    const activePlayers = currentCourts.reduce((active, court) => {
      if (court.currentGame && court.isActive) {
        const courtPlayers = [
          ...court.currentGame.players.team1,
          ...court.currentGame.players.team2
        ];
        console.log(`Court ${court.id} active: ${courtPlayers.join(', ')}`);
        return [...active, ...courtPlayers];
      } else {
        console.log(`Court ${court.id} idle`);
      }
      return active;
    }, []);
    
    // Get all 16 players
    const allPlayers = Object.values(teamData).reduce((players, team) => {
      return [
        ...players, 
        getDisplayName(team.f1), 
        getDisplayName(team.f2), 
        getDisplayName(team.m1), 
        getDisplayName(team.m2)
      ];
    }, []);
    
    // Calculate available players (all players not currently playing)
    const availablePlayers = allPlayers.filter(player => !activePlayers.includes(player));
    
    console.log(`Active players: ${activePlayers.length}`);
    console.log(`Available players: ${availablePlayers.length} - ${availablePlayers.join(', ')}`);
    console.log(`Total check: ${activePlayers.length + availablePlayers.length}/16`);
    
    // Convert display names back to full names for game matching
    const availablePlayerFullNames = availablePlayers.map(displayName => {
      const fullName = Object.keys(displayNameMap).find(key => displayNameMap[key] === displayName);
      return fullName || displayName;
    });
    
    return availablePlayerFullNames;
  };

  // Legacy function - use calculateAvailablePlayersFromState instead
  const getCurrentAvailablePlayers = () => {
    return calculateAvailablePlayersFromState(courts);
  };

  // Select 3 games with NO player conflicts
  const selectInitialGames = () => {
    const allGames = gameManager.getGamesByStatus('pending');
    const selectedGames = [];
    const usedPlayers = new Set();

    console.log('=== SELECTING INITIAL GAMES (NO CONFLICTS) ===');

    for (const game of allGames) {
      const gamePlayerNames = [
        ...game.players.team1.map(getPlayerName),
        ...game.players.team2.map(getPlayerName)
      ];

      const hasConflict = gamePlayerNames.some(player => usedPlayers.has(player));

      if (!hasConflict && selectedGames.length < 3) {
        selectedGames.push(game);
        gamePlayerNames.forEach(player => usedPlayers.add(player));
        
        console.log(`Selected Game ${game.id}: ${getTeamName(game.team1)} vs ${getTeamName(game.team2)} (${game.gameTypeLabel})`);
        console.log(`  Players: ${gamePlayerNames.join(', ')}`);
      } else if (hasConflict) {
        console.log(`Skipped Game ${game.id}: Player conflict with previous games`);
      }

      if (selectedGames.length === 3) break;
    }

    console.log(`=== FINAL SELECTION: ${selectedGames.length} games ===`);
    console.log(`Used players: ${Array.from(usedPlayers).join(', ')}`);
    
    return selectedGames;
  };

  // Start the tournament with conflict-free games
  const startTournament = () => {
    const now = Date.now();
    setIsSessionStarted(true);
    
    const availableGames = selectInitialGames();
    
    if (availableGames.length < 3) {
      console.error('Could not find 3 conflict-free games to start tournament');
      return;
    }
    
    const updatedCourts = courts.map((court, index) => {
      if (availableGames[index]) {
        const game = availableGames[index];
        gameManager.assignGameToCourt(game.id, court.id);
        
        const courtGame = {
          ...game,
          players: {
            team1: game.players.team1.map(getDisplayName),
            team2: game.players.team2.map(getDisplayName)
          }
        };
        
        return {
          ...court,
          currentGame: courtGame,
          players: [...courtGame.players.team1, ...courtGame.players.team2],
          isActive: true,
          timer: { startTime: now, isActive: true, duration: 15 * 60 }
        };
      }
      return court;
    });
    
    setCourts(updatedCourts);
    gameManager.updatePlayerStatus(updatedCourts);
    updateWaitingPlayers();
  };

  // Update waiting players based on current court state
  const updateWaitingPlayers = () => {
    const availablePlayerFullNames = getCurrentAvailablePlayers();
    const availableDisplayNames = availablePlayerFullNames.map(fullName => 
      displayNameMap[fullName] || fullName
    );
    
    setWaitingPlayers(availableDisplayNames);
  };

  // Check if a game can be played with available players
  const canPlayGameWithPlayers = (game, availablePlayerNames) => {
    const gamePlayerNames = [
      ...game.players.team1.map(player => player.split('(')[0].trim()),
      ...game.players.team2.map(player => player.split('(')[0].trim())
    ];
    
    return gamePlayerNames.every(playerName => availablePlayerNames.includes(playerName));
  };

  // Get player names from a game
  const getGamePlayerNames = (game) => {
    return [
      ...game.players.team1.map(player => player.split('(')[0].trim()),
      ...game.players.team2.map(player => player.split('(')[0].trim())
    ];
  };

  // Format game for display
  const formatGame = (game) => {
    return {
      ...game,
      players: {
        team1: game.players.team1.map(getDisplayName),
        team2: game.players.team2.map(getDisplayName)
      }
    };
  };

  // Backfill idle courts when new games become available
  const backfillIdleCoursWithState = (currentCourts) => {
    const idleCourts = currentCourts.filter(c => !c.isActive && !c.currentGame);
    if (idleCourts.length === 0) {
      console.log('No idle courts to backfill');
      return;
    }

    const availablePlayers = calculateAvailablePlayersFromState(currentCourts);
    console.log(`BACKFILL: Checking ${idleCourts.length} idle courts with ${availablePlayers.length} available players`);

    if (availablePlayers.length < 4) {
      console.log('Not enough players for backfill');
      return;
    }

    // Find all possible games
    const pendingGames = gameManager.getGamesByStatus('pending');
    const viableGames = pendingGames.filter(game => 
      canPlayGameWithPlayers(game, availablePlayers)
    );

    if (viableGames.length === 0) {
      console.log('No viable games for backfill');
      return;
    }

    console.log(`Found ${viableGames.length} viable games for ${idleCourts.length} idle courts`);

    // Assign games to idle courts without player conflicts
    const assignments = [];
    const usedPlayers = new Set();

    for (const game of viableGames) {
      if (assignments.length >= idleCourts.length) break;
      
      const gamePlayerNames = getGamePlayerNames(game);
      const hasConflict = gamePlayerNames.some(p => usedPlayers.has(p));
      
      if (!hasConflict) {
        const court = idleCourts[assignments.length];
        assignments.push({ court, game });
        gamePlayerNames.forEach(p => usedPlayers.add(p));
        
        console.log(`Backfill assignment: Court ${court.id} gets Game ${game.id} (${game.team1} vs ${game.team2})`);
      }
    }

    // Apply assignments by triggering match suggestions
    if (assignments.length > 0) {
      console.log(`BACKFILLING ${assignments.length} courts`);
      
      // Suggest the first assignment
      const firstAssignment = assignments[0];
      setSuggestedMatch({ 
        game: formatGame(firstAssignment.game), 
        courtId: firstAssignment.court.id 
      });
    }
  };

  // Legacy backfill function
  const backfillIdleCourts = () => {
    backfillIdleCoursWithState(courts);
  };

  // Handle score submission with robust player accounting
  const handleScoreSubmit = (courtId, team1Score, team2Score) => {
    const court = courts.find(c => c.id === courtId);
    if (!court || !court.currentGame) return;

    // Validate scores - prevent 0-0 games
    if (team1Score === 0 && team2Score === 0) {
      alert('Please enter valid scores. Games cannot end 0-0.');
      return;
    }

    const game = court.currentGame;
    
    // Regular tournament game
    gameManager.recordGameResult(game.id, team1Score, team2Score);
    
    console.log(`\n=== MLP GAME COMPLETE: Court ${courtId} finished ${team1Score}-${team2Score} ===`);
    console.log(`Game ${game.id} completed: ${getTeamName(game.team1)} vs ${getTeamName(game.team2)}`);
    
    // Check if tournament is complete
    if (gameManager.isTournamentComplete()) {
      console.log('Tournament is complete! Getting results...');
      const results = gameManager.getTournamentResults();
      console.log('Tournament results:', results);
      
      // SIMPLIFIED: Just complete the tournament and let parent handle championship final
      onSessionComplete(results);
      return;
    }
    
    // Clear the finished court and calculate available players with NEW state
    setCourts(prev => {
      const newCourts = prev.map(c => 
        c.id === courtId ? 
          { ...c, players: [], currentGame: null, isActive: false } : 
          c
      );
      
      gameManager.updatePlayerStatus(newCourts);
      
      // CRITICAL: Use the NEW court state immediately for calculations
      console.log('=== USING NEW COURT STATE FOR PLAYER CALCULATIONS ===');
      const availablePlayerNames = calculateAvailablePlayersFromState(newCourts);
      
      console.log(`Available players count: ${availablePlayerNames.length}`);
      
      if (availablePlayerNames.length < 4) {
        console.log('Not enough players available for next game');
        // Update waiting players using new state
        const availableDisplayNames = availablePlayerNames.map(fullName => 
          displayNameMap[fullName] || fullName
        );
        setWaitingPlayers(availableDisplayNames);
        return newCourts;
      }

      // Find next available game for this specific court
      const nextGame = findBestAvailableGame(availablePlayerNames);
      
      if (nextGame) {
        console.log(`Next game found for Court ${courtId}: ${nextGame.team1} vs ${nextGame.team2} - ${nextGame.gameTypeLabel}`);
        
        const formattedGame = {
          ...nextGame,
          players: {
            team1: nextGame.players.team1.map(getDisplayName),
            team2: nextGame.players.team2.map(getDisplayName)
          }
        };
        
        setSuggestedMatch({ game: formattedGame, courtId: courtId });
      } else {
        console.log('No games available for this court');
      }

      // Update waiting players using new state
      const availableDisplayNames = availablePlayerNames.map(fullName => 
        displayNameMap[fullName] || fullName
      );
      setWaitingPlayers(availableDisplayNames);

      // CRITICAL: After handling this court, check if other idle courts can be filled using NEW state
      setTimeout(() => {
        backfillIdleCoursWithState(newCourts);
      }, 200);
      
      console.log('=== MLP SCORE SUBMIT COMPLETE ===\n');
      
      return newCourts;
    });
  };

  // Handle match suggestion acceptance
  const handleAcceptMatch = () => {
    if (!suggestedMatch) return;
    
    const { game, courtId } = suggestedMatch;
    gameManager.assignGameToCourt(game.id, courtId);
    
    setCourts(prev => {
      const newCourts = prev.map(court => 
        court.id === courtId ? {
          ...court,
          currentGame: game,
          players: [...game.players.team1, ...game.players.team2],
          isActive: true,
          timer: { startTime: Date.now(), isActive: true, duration: 15 * 60 }
        } : court
      );
      
      gameManager.updatePlayerStatus(newCourts);
      
      // CRITICAL: After assigning this game, check if other courts still need games
      // Use immediate execution since we have the correct newCourts state
      backfillIdleCoursWithState(newCourts);
      
      return newCourts;
    });
    
    setSuggestedMatch(null);
    updateWaitingPlayers();
  };

  // Handle match suggestion rejection
  const handleRejectMatch = () => {
    setSuggestedMatch(null);
    updateWaitingPlayers();
    
    // Check if other courts can be filled after rejecting this match
    setTimeout(() => {
      backfillIdleCourts();
    }, 100);
  };

  // Get tournament data for sidebar
  const getTournamentData = () => {
    const progress = gameManager.getTournamentProgress();
    const standings = gameManager.calculateStandings();
    const remainingGames = gameManager.getRemainingGamesBreakdown();
    
    return {
      progress,
      standings,
      remainingGames,
      waitingPlayers,
      tournamentComplete: gameManager.isTournamentComplete()
    };
  };

  const tournamentData = getTournamentData();

  return (
    <div className="game-interface">
      <div className="game-content">
        <div className="courts-section">
          {!isSessionStarted ? (
            <div className="session-start">
              <h2>MLP Tournament Ready</h2>
              <p>4 teams, 16 players, 24 games</p>
              <p style={{ opacity: 0.8, marginBottom: '20px' }}>
                Round Robin Format | 3 Courts | First to 15, Win by 2 | Dynamic Scheduling
              </p>
              <div style={{ marginBottom: '20px', fontSize: '14px', opacity: 0.7 }}>
                <strong>Teams:</strong><br/>
                {Object.entries(teamData).map(([teamId, team]) => (
                  <div key={teamId} style={{ marginBottom: '4px' }}>
                    {getTeamName(teamId)}: {getDisplayName(team.m1)}, {getDisplayName(team.m2)}, {getDisplayName(team.f1)}, {getDisplayName(team.f2)}
                  </div>
                ))}
              </div>
              <button onClick={startTournament} className="start-session-button">
                Start Tournament
              </button>
            </div>
          ) : (
            <div className="courts-container">
              {courts.slice().reverse().map((court) => (
                <Court
                  key={court.id}
                  courtData={court}
                  onScoreSubmit={handleScoreSubmit}
                  allPlayers={[]}
                  competitionLevel="tournament"
                  gameInfo={court.currentGame}
                  getTeamName={getTeamName}
                />
              ))}
            </div>
          )}
        </div>
        
        <Sidebar 
          mode="mlp"
          tournamentData={tournamentData}
          getTeamName={getTeamName}
        />
      </div>

      {suggestedMatch && (
        <MatchSuggestionPopup
          suggestedGame={suggestedMatch.game}
          courtId={suggestedMatch.courtId}
          onAccept={handleAcceptMatch}
          onWait={handleRejectMatch}
          onClose={handleRejectMatch}
          getTeamName={getTeamName}
        />
      )}
    </div>
  );
};

export default MLPGameInterface;